import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../core";
import {
  BookOpenIcon,
  PencilSquareIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

const SingleAssignmentTab = ({
  activeTab,
  setActiveTab,
  assignmentId,
}: {
  activeTab: "overview" | "edit" | "analytics";
  setActiveTab: Dispatch<SetStateAction<"overview" | "edit" | "analytics">>;
  assignmentId: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tabs = [
    {
      id: "overview",
      label: t("assignments.tabs.overview"),
      path: `/assignments/${assignmentId}`,
      icon: BookOpenIcon,
    },
    {
      id: "edit",
      label: t("assignments.tabs.questions"),
      path: `/assignments/${assignmentId}/edit`,
      icon: PencilSquareIcon,
    },
    {
      id: "analytics",
      label: t("assignments.tabs.analytics"),
      path: `/assignments/${assignmentId}/analytics`,
      icon: ChartBarIcon,
    },
  ] as const;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-sm mb-4 sm:mb-6 overflow-hidden">
      <div className="relative">
        <nav className="flex overflow-x-auto scrollbar-none">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <Button
                key={tab.id}
                size="md"
                textColor={
                  isActive
                    ? "text-primary-600 dark:text-primary-300"
                    : "text-gray-800 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
                }
                className={`
                  flex-1 px-3 py-4 text-sm sm:text-base font-medium
                  transition-all duration-200 ease-in-out
                  relative group
                  ${isActive ? "font-semibold cursor-default" : ""}
                  ${!isActive ? "hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md active:shadow-inner" : ""}
                `}
                onClick={() => {
                  if (!isActive) {
                    setActiveTab(tab.id as typeof activeTab);
                    navigate(tab.path, { replace: true });
                  }
                }}
              >
                <span className="relative inline-flex items-center gap-2">
                  <tab.icon
                    className={`w-5 h-5 ${
                      isActive
                        ? "text-primary-600 dark:text-primary-300"
                        : "text-gray-800 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white"
                    }`}
                  />
                  {tab.label}
                </span>
                <div
                  className={`
                    absolute inset-0 opacity-0 bg-primary-100 dark:bg-primary-900/20
                    transition-opacity duration-200 -z-10
                    ${isActive ? "opacity-20" : "group-hover:opacity-10"}
                  `}
                />
              </Button>
            );
          })}
        </nav>
        <div
          className="absolute bottom-0 h-1.5 bg-primary-500 dark:bg-primary-400 transition-all duration-300 rounded-full shadow-sm"
          style={{
            left: `${(tabs.findIndex((tab) => tab.id === activeTab) * 100) / 3 + 5}%`,
            width: "23.333%",
          }}
        />
      </div>
    </div>
  );
};

export default SingleAssignmentTab;
